<script setup lang="ts">
import { onMounted, ref } from 'vue';
import type { NestedFileResponse } from 'src/api/models';
import { getFetchFile } from 'src/api/queries/order-portal-bff';
import { useFileUpload } from 'src/composables/useFileUpload';
import SharedGallery from '../shared/SharedGallery.vue';
import TicketEditSectionHeader from './TicketEditSectionHeader.vue';

const props = defineProps<{
  files: NestedFileResponse[];
  ticketId: string;
}>();

const { uploadFilesToTicket } = useFileUpload();

const pictures = ref<string[]>([]);
const loading = ref(false);
const uploading = ref(false);
const isDragging = ref(false);
const fileInputRef = ref<HTMLInputElement>();

async function loadExistingFiles() {
  const fileIds = props.files?.map((f) => f.id).filter(Boolean) as string[];
  if (!fileIds.length) return;

  loading.value = true;
  try {
    await Promise.all(
      fileIds.map(async (fileId) => {
        const response = await getFetchFile(fileId, { responseType: 'blob' });
        const blob = new Blob([response as any]);
        pictures.value.push(URL.createObjectURL(blob));
      }),
    );
  } finally {
    loading.value = false;
  }
}

async function handleFiles(files: FileList | File[]) {
  if (!files.length) return;
  uploading.value = true;
  try {
    const fileArray = Array.from(files);
    await uploadFilesToTicket(props.ticketId, fileArray);
    for (const file of fileArray) {
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result) pictures.value.push(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  } finally {
    uploading.value = false;
  }
}

function handleDrop(event: DragEvent) {
  isDragging.value = false;
  if (event.dataTransfer?.files) {
    handleFiles(event.dataTransfer.files);
  }
}

function handleFileSelect(event: Event) {
  const input = event.target as HTMLInputElement;
  if (input.files) {
    handleFiles(input.files);
    input.value = '';
  }
}

onMounted(() => loadExistingFiles());
</script>

<template>
  <div class="p-8">
    <TicketEditSectionHeader title="Media" :count="pictures.length" class="mb-4" />

    <div
      class="upload-zone"
      :class="{ 'upload-zone-active': isDragging, 'upload-zone-uploading': uploading }"
      @dragover.prevent="isDragging = true"
      @dragleave="isDragging = false"
      @drop.prevent="handleDrop"
      @click="fileInputRef?.click()"
    >
      <i v-if="uploading" class="pi pi-spinner pi-spin" style="font-size: 24px" />
      <i v-else class="pi pi-cloud-upload" style="font-size: 24px" />
      <p class="text-sm mt-2">
        <template v-if="uploading">Uploading...</template>
        <template v-else>
          Drop files here or <span class="upload-link">browse</span>
        </template>
      </p>
      <p v-if="!uploading" class="text-xs mt-1 opacity-50">Images up to 1MB</p>
      <input
        ref="fileInputRef"
        type="file"
        class="hidden"
        multiple
        accept="image/*"
        @change="handleFileSelect"
        @click.stop
      />
    </div>

    <div v-if="loading" class="flex items-center justify-center py-8">
      <i class="pi pi-spinner pi-spin text-surface-400" style="font-size: 20px" />
    </div>

    <div v-else-if="pictures.length" class="mt-6">
      <SharedGallery :images="pictures" />
    </div>

    <div v-else-if="!loading" class="empty-state">
      <i class="pi pi-images" style="font-size: 28px" />
      <p>No media files yet</p>
    </div>
  </div>
</template>

<style scoped>
.upload-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  border: 2px dashed var(--p-surface-200);
  border-radius: 0.75rem;
  color: var(--p-surface-400);
  cursor: pointer;
  transition: all 0.2s;
}
:root[class*='app-dark'] .upload-zone {
  border-color: var(--p-surface-700);
}
.upload-zone:hover {
  border-color: var(--p-primary-300);
  color: var(--p-primary-500);
  background: color-mix(in srgb, var(--p-primary-500) 3%, transparent);
}
:root[class*='app-dark'] .upload-zone:hover {
  border-color: var(--p-primary-600);
  color: var(--p-primary-400);
  background: color-mix(in srgb, var(--p-primary-500) 5%, transparent);
}
.upload-zone-active {
  border-color: var(--p-primary-400);
  background: color-mix(in srgb, var(--p-primary-500) 5%, transparent);
  color: var(--p-primary-500);
}
.upload-zone-uploading {
  pointer-events: none;
  opacity: 0.7;
}

.upload-link {
  color: var(--p-primary-500);
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
}

</style>
